/**
 * Lista de llaves permitidas para el almacenamiento.
 * Centralizar las llaves aquí evita errores de escritura (typos) en la aplicación.
 */
export type StorageKey = 'state_url' | 'customer_id' | 'user_email' | 'theme_preference';

/**
 * Utilidad agnóstica para la gestión de persistencia en sessionStorage.
 * Permite manejar cualquier llave definida en StorageKey con soporte para tipos complejos.
 */
export class StorageUtils {

  /** * Llave utilizada para almacenar la URL de redirección en el almacenamiento de sesión.
   * @private
   */
  public static readonly STATE_KEY = 'state_url';

  /**
   * Guarda un valor en el almacenamiento.
   * Si el valor es un objeto o array, lo serializa automáticamente a JSON.
   * * @param key - Llave de destino (definida en StorageKey).
   * @param value - Valor a guardar (string, número, objeto, etc).
   */
  static set<T>(key: StorageKey, value: T): void {
    const data = typeof value === 'string' ? value : JSON.stringify(value);
    sessionStorage.setItem(key, data);
  }

  /**
   * Recupera un valor del almacenamiento.
   * Intenta parsear el contenido como JSON si es posible.
   * * @param key - Llave a consultar.
   * @returns El valor recuperado con su tipo correspondiente o null si no existe.
   */
  static get<T = string>(key: StorageKey): T | null {
    const data = sessionStorage.getItem(key);
    if (!data) return null;

    try {
      // Intentamos parsear por si es un objeto guardado con JSON.stringify
      return JSON.parse(data) as T;
    } catch {
      // Si falla el parse, es un string simple
      return data as unknown as T;
    }
  }

  /**
   * Recupera un valor y lo elimina inmediatamente del almacenamiento.
   * Útil para datos de un solo uso como tokens temporales o URLs de redirección.
   * * @param key - Llave a recuperar y borrar.
   * @returns El valor recuperado o null.
   */
  static pop<T = string>(key: StorageKey): T | null {
    const value = this.get<T>(key);
    this.remove(key);
    return value;
  }

  /**
   * Elimina una llave específica del almacenamiento.
   * @param key - Llave a eliminar.
   */
  static remove(key: StorageKey): void {
    sessionStorage.removeItem(key);
  }

  /**
   * Limpia todo el contenido del almacenamiento de sesión.
   */
  static clear(): void {
    sessionStorage.clear();
  }
}